import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Select,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Button,
    Divider,
    Box,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter, ManageDialogPrimaryButton } from '../manage/ManageDialogFrame';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import {
    BuildingType,
    BUILDING_TYPES,
    CLIMATE_PRESETS,
    DEFAULT_ASSEMBLIES,
} from '../../tools/analyze/energyEngine';

/**
 * Analyze > Energy Settings dialog: building type, design temperatures,
 * utility rates and carbon intensity used by the energy engine.
 */
export function EnergySettingsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.manageDialog === 'analyzeEnergySettings');
    const onClose = useAppStore((s) => s.closeManageDialog);
    const settings = useAnalysisStore((s) => s.energySettings);
    const setSettings = useAnalysisStore((s) => s.setEnergySettings);

    const [form, setForm] = useState(settings);
    useEffect(() => {
        if (isOpen) setForm(settings);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const apply = () => {
        setSettings(form);
        onClose();
    };

    const numField = (label: string, key: keyof typeof form, step = 0.5, suffix?: string) => (
        <HStack justify="space-between">
            <Text fontSize="sm" color="var(--color-text-secondary)" w="190px">{label}</Text>
            <NumberInput
                size="sm"
                w="110px"
                step={step}
                value={String(form[key])}
                onChange={(_, v) => Number.isFinite(v) && setForm(f => ({ ...f, [key]: v }))}
            >
                <NumberInputField />
                <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                </NumberInputStepper>
            </NumberInput>
            {suffix && <Text fontSize="xs" w="60px" color="var(--color-text-secondary)">{suffix}</Text>}
        </HStack>); return (<ManageDialogFrame
            title={t('analyze.energySettings')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={<ManageDialogPrimaryButton label={t('analyze.oK')} onClick={apply} />}
                />
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)" w="190px">{t('analyze.buildingType')}</Text>
                    <Select
                        size="sm"
                        value={form.buildingType}
                        onChange={e => setForm(f => ({ ...f, buildingType: e.target.value as BuildingType }))}
                        maxW="180px"
                    >
                        {BUILDING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                </HStack>
                {numField('Heating Setpoint', 'heatingSetpointC', 0.5, '°C')}
                {numField('Cooling Setpoint', 'coolingSetpointC', 0.5, '°C')}
                <Divider />
                <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>{t('analyze.designConditions')}</Text>
                    <HStack mb={2}>
                        <Text fontSize="xs" color="var(--color-text-secondary)" w="190px">{t('analyze.climatePreset')}</Text>
                        <Select
                            size="sm"
                            placeholder={t('analyze.select')}
                            maxW="180px"
                            onChange={e => {
                                const p = CLIMATE_PRESETS.find(x => x.name === e.target.value);
                                if (p) setForm(f => ({
                                    ...f,
                                    winterDesignTempC: p.winterDesignTempC,
                                    summerDesignTempC: p.summerDesignTempC,
                                    summerDesignWetBulbC: p.summerDesignWetBulbC,
                                }));
                            }}
                        >
                            {CLIMATE_PRESETS.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                        </Select>
                    </HStack>
                    {numField('Winter Design Dry-Bulb', 'winterDesignTempC', 1, '°C')}
                    {numField('Summer Design Dry-Bulb', 'summerDesignTempC', 1, '°C')}
                    {numField('Summer Design Wet-Bulb', 'summerDesignWetBulbC', 1, '°C')}
                </Box>
                <Divider />
                <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>{t('analyze.ratesCarbon')}</Text>
                    {numField('Electricity Rate', 'electricityRate', 0.01, '/kWh')}
                    {numField('Gas Rate', 'gasRate', 0.005, '/kWh')}
                    {numField('Grid Carbon Intensity', 'carbonIntensity', 0.05, 'kg/kWh')}
                </Box>
                <Box>
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        Default assemblies: wall U {DEFAULT_ASSEMBLIES.wallU} · glazing U {DEFAULT_ASSEMBLIES.glazingU} ·
                        roof U {DEFAULT_ASSEMBLIES.roofU} W/m²K · SHGC {DEFAULT_ASSEMBLIES.shgc}
                    </Text>
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
